import React, { useState, useEffect, useContext, useRef } from 'react'
import './style.scss'
import { useQuery } from 'react-query'
import { toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import Icon from 'react-icons-kit'
import {
    users, heart, clock, send, slash, fileText, checkCircle, camera,
    creditCard, calendar, trendingUp, arrowRight, eye, zap, shield
} from 'react-icons-kit/feather'
import { Images } from '../../../utils/Images'
import Requests from '../../../utils/Requests/Index'
import ImageCrop from '../../../components/Form/Picture'
import MatchCard from '../../../components/MyBiye/MatchCard/Index'
import Loading from '../../../components/Loading/Index'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { singleProContext } from '../../../utils/context/SingleProfileContext'
import { HeightCalculate } from '../../../utils/helpers'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Navigation as SwiperNavigation, Autoplay } from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/navigation'

// Dashboard helpers
const formatDate = value => {
    if (!value) return null
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return null
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}

const daysLeft = value => {
    if (!value) return null
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return null
    return Math.ceil((date.getTime() - Date.now()) / 86400000)
}

const Index = () => {
    let { search } = useLocation()
    let navigate = useNavigate()
    const { handleLocalStoreIds } = useContext(singleProContext);
    const [show, setShow] = useState(false)
    const [header] = useState({ headers: { Authorization: "Bearer " + localStorage.getItem('token') } })
    let [pageLoad, setPageload] = useState(true)

    // Recently viewed slider navigation
    const prevRef = useRef(null)
    const nextRef = useRef(null)

    // Fetch data
    const getDashboardData = async () => {
        try {
            const response = await Requests.Dashboard.Index(header)
            if (response && response.user) {
                setPageload(false)
            }
            return response?.user
        } catch (error) {
            console.log(error.message)
        }
    }

    const { data, isLoading, isError } = useQuery("dashboard", getDashboardData, {
        retry: false,
        cacheTime: 1200000,
        refetchOnWindowFocus: false,
        enabled: Boolean(pageLoad)
    })

    //after payment
    useEffect(() => {
        const status = (search || '').split('=')[1]
        if (status === "success") {
            toast.success('Congratulations, Payment Successful')
        }
        else if (status === 'failed') {
            toast.error('Payment is not Successful, try again')
        }
        else if (status === 'cancle') {
            toast.error('Payment cancled, try again')
        }
    }, [search])

    useEffect(() => {
        // Set ids For Single Profile...
        let recentViewIds = []
        let matchIds = []
        if (data?.recentViewed) {
            recentViewIds = data?.recentViewed?.map(info => info._id);
        }
        if (data?.matches) {
            matchIds = data?.matches?.map(info => info._id);
        }
        handleLocalStoreIds([...recentViewIds, ...matchIds]);
    }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

    const toPackage = e => {
        e.preventDefault()
        navigate('/my-dashboard/package')
    }

    const toMyProfile = e => {
        e.preventDefault()
        navigate('/my-dashboard/profile')
    }

    if (isLoading || isError) return <Loading />

    const user = data || {}
    const matches = user.matches || []
    const visibleMatches = matches.slice(0, 6)
    const recentViewed = user.recentViewed || []
    const expireDate = formatDate(user.packageExpire)
    const remainingDays = daysLeft(user.packageExpire)
    const isPaid = user.profileType === 'Paid'
    const completion = Math.min(Math.max(parseInt(user.updateRange, 10) || 0, 0), 100)
    const proposalLimit = user.packageInfo?.totalLimit != null ? user.packageInfo.totalLimit : 0
    const contactViewLimit = user.packageInfo?.contactViewLimit != null ? user.packageInfo.contactViewLimit : 0

    // Activity summary tiles
    const stats = [
        { key: 'connection', label: 'Connections', value: user.connection || 0, icon: users, tone: 'teal', to: '/connection' },
        { key: 'favorite', label: 'Favorites', value: user.favorite || 0, icon: heart, tone: 'rose', to: null },
        { key: 'pending', label: 'Pending Requests', value: user.pendingRequest || 0, icon: clock, tone: 'amber', to: '/connection/pending' },
        { key: 'sent', label: 'Sent Requests', value: user.sentRequest || 0, icon: send, tone: 'indigo', to: '/connection/send' },
        { key: 'block', label: 'Blocked', value: user.block || 0, icon: slash, tone: 'slate', to: null },
        { key: 'posts', label: 'Timeline Posts', value: user.posts || 0, icon: fileText, tone: 'violet', to: '/timeline' },
    ]

    return (
        <div className="dashboard">
            <div className="container">

                {/* ============ HERO ============ */}
                <section className="dash-hero">
                    <span className="dash-glow dash-glow-1" />
                    <span className="dash-glow dash-glow-2" />

                    <div className="dash-hero-inner">
                        <div
                            className="dash-avatar"
                            role="button"
                            tabIndex={0}
                            title="Change profile picture"
                            onClick={() => setShow(true)}
                            onKeyDown={e => e.key === 'Enter' && setShow(true)}
                        >
                            <img src={user.image ? user.image : Images.NoImage} alt={user.name || 'Profile'} />
                            <span className="dash-avatar-edit"><Icon icon={camera} size={15} /></span>
                        </div>

                        <div className="dash-hero-info">
                            <div className="dash-hero-name">
                                <h4 className="text-capitalize mb-0">{user.name || 'Your Name'}</h4>
                                {user.isPhoneVerified
                                    ? <span className="dash-verified"><Icon icon={checkCircle} size={13} /> Verified</span>
                                    : <span className="dash-unverified"><Icon icon={shield} size={13} /> Not verified</span>}
                            </div>

                            <p className="dash-pid">ID: {user.profileId || '—'}</p>

                            <div className="dash-chips">
                                <span className="dash-chip">
                                    <Icon icon={zap} size={13} /> {user.profileType || 'Free'} Membership
                                </span>
                                <span className="dash-chip">
                                    <Icon icon={calendar} size={13} />
                                    {expireDate
                                        ? (remainingDays > 0 ? `Expires ${expireDate}` : `Expired on ${expireDate}`)
                                        : 'No active package'}
                                </span>
                                {remainingDays > 0 &&
                                    <span className="dash-chip dash-chip-accent">{remainingDays} days left</span>
                                }
                            </div>
                        </div>

                        <div className="dash-hero-actions">
                            <button type="button" className="dash-btn dash-btn-ghost" onClick={toMyProfile}>
                                <Icon icon={users} size={15} /> My Profile
                            </button>
                            <button type="button" className="dash-btn dash-btn-light" onClick={toPackage}>
                                <Icon icon={creditCard} size={15} /> {isPaid ? 'Manage Package' : 'Upgrade Now'}
                            </button>
                        </div>
                    </div>
                </section>

                {/* ============ ACTIVITY SUMMARY ============ */}
                <section className="dash-stats">
                    {stats.map(item => {
                        const inner = (
                            <>
                                <span className="dash-stat-icon"><Icon icon={item.icon} size={18} /></span>
                                <h3 className="dash-stat-value">{item.value}</h3>
                                <p className="dash-stat-label">{item.label}</p>
                            </>
                        )
                        return item.to
                            ? <Link to={item.to} key={item.key} className={`dash-stat tone-${item.tone}`}>{inner}</Link>
                            : <div key={item.key} className={`dash-stat tone-${item.tone}`}>{inner}</div>
                    })}
                </section>

                {/* ============ MY MATCHES ============ */}
                <section className="dash-matches">
                    <div className="dash-panel-head">
                        <h5 className="dash-panel-title"><Icon icon={heart} size={16} /> My Matches</h5>
                        <Link to="/matches/all" className="dash-view-all">
                            View all <Icon icon={arrowRight} size={13} />
                        </Link>
                    </div>

                    {matches.length ? (
                        <>
                            <div className="dash-matches-grid">
                                {visibleMatches.map(item => <MatchCard key={item._id} item={item} />)}
                            </div>

                            {matches.length > visibleMatches.length ?
                                <Link to="/matches/all" className="dash-matches-more">
                                    View all {matches.length} matches <Icon icon={arrowRight} size={14} />
                                </Link>
                                : null}
                        </>
                    ) : (
                        <div className="dash-matches-empty">
                            <span className="dash-stat-icon"><Icon icon={users} size={18} /></span>
                            <div>
                                <h6 className="mb-1">No matches found yet</h6>
                                <p className="mb-0">Complete your partner preference to get better matches.</p>
                            </div>
                            <Link to="/my-dashboard/partner-preference" className="dash-view-all">
                                Set preference <Icon icon={arrowRight} size={13} />
                            </Link>
                        </div>
                    )}
                </section>

                {/* ============ MEMBERSHIP + PROFILE STRENGTH ============ */}
                <section className="dash-panels">

                    {/* Membership */}
                    <div className="dash-panel">
                        <div className="dash-panel-head">
                            <h5 className="dash-panel-title"><Icon icon={creditCard} size={16} /> Membership</h5>
                            <span className={`dash-plan-badge ${isPaid ? 'is-paid' : 'is-free'}`}>
                                {user.profileType || 'Free'}
                            </span>
                        </div>

                        <div className="dash-plan-rows">
                            <div className="dash-plan-row">
                                <span className="dash-plan-icon"><Icon icon={heart} size={15} /></span>
                                <div className="dash-plan-text">
                                    <p>Proposal limit</p>
                                    <h6>{proposalLimit} <small>remaining</small></h6>
                                </div>
                            </div>

                            <div className="dash-plan-row">
                                <span className="dash-plan-icon"><Icon icon={eye} size={15} /></span>
                                <div className="dash-plan-text">
                                    <p>Contact view limit</p>
                                    <h6>{contactViewLimit} <small>remaining</small></h6>
                                </div>
                            </div>

                            <div className="dash-plan-row">
                                <span className="dash-plan-icon"><Icon icon={calendar} size={15} /></span>
                                <div className="dash-plan-text">
                                    <p>Expire date</p>
                                    <h6>{expireDate || <span className="dash-link" onClick={toPackage}>Upgrade to activate</span>}</h6>
                                </div>
                            </div>
                        </div>

                        <button type="button" className="dash-btn dash-btn-primary dash-btn-full" onClick={toPackage}>
                            {isPaid ? 'Renew or upgrade package' : 'Upgrade to premium'}
                        </button>
                    </div>

                    {/* Profile strength */}
                    <div className="dash-panel">
                        <div className="dash-panel-head">
                            <h5 className="dash-panel-title"><Icon icon={trendingUp} size={16} /> Profile Strength</h5>
                            <Link to="/my-dashboard/profile" className="dash-view-all">Improve <Icon icon={arrowRight} size={13} /></Link>
                        </div>

                        <div className="dash-progress">
                            <div className="dash-ring" style={{ '--pct': `${completion}%` }}>
                                <span>{completion}%</span>
                            </div>
                            <div className="dash-progress-text">
                                <h6>{completion >= 100 ? 'Your profile is complete' : 'Your profile is incomplete'}</h6>
                                <p>
                                    {completion >= 100
                                        ? 'Great job! Keep it updated to stay visible in searches.'
                                        : 'Add contact details, ID card information and photos to get more matches.'}
                                </p>
                            </div>
                        </div>

                        <div className="dash-bar">
                            <span style={{ width: `${completion}%` }} />
                        </div>

                        <button type="button" className="dash-btn dash-btn-outline dash-btn-full" onClick={toMyProfile}>
                            Complete my profile
                        </button>
                    </div>

                </section>

                {/* ============ RECENTLY VIEWED ============ */}
                <section className="dash-viewers-wrap">
                    <div className="dash-panel-head">
                        <h5 className="dash-panel-title"><Icon icon={eye} size={16} /> Recently Viewed You</h5>
                        <div className="dash-head-right">
                            <Link to="/matches/recently-viewed" className="dash-view-all">
                                View all <Icon icon={arrowRight} size={13} />
                            </Link>
                            {recentViewed.length ?
                                <div className="dash-nav">
                                    <button type="button" ref={prevRef} aria-label="Previous">←</button>
                                    <button type="button" ref={nextRef} aria-label="Next">→</button>
                                </div>
                                : null}
                        </div>
                    </div>

                    {recentViewed.length ? (
                        <div className="dash-viewers">
                            <Swiper
                                modules={[SwiperNavigation, Autoplay]}
                                spaceBetween={16}
                                rewind={true}
                                autoplay={{ delay: 3200, disableOnInteraction: false }}
                                onBeforeInit={swiper => {
                                    swiper.params.navigation.prevEl = prevRef.current
                                    swiper.params.navigation.nextEl = nextRef.current
                                }}
                                navigation={{ prevEl: prevRef.current, nextEl: nextRef.current }}
                                breakpoints={{
                                    0: { slidesPerView: 1.15 },
                                    576: { slidesPerView: 2.2 },
                                    992: { slidesPerView: 3.2 },
                                    1400: { slidesPerView: 4 },
                                }}
                            >
                                {recentViewed.map(item => (
                                    <SwiperSlide key={item._id}>
                                        <Link to={`/single-profile/${item._id}`} className="dash-viewer">
                                            <div className="dash-viewer-img">
                                                <img
                                                    src={item?.images?.[0]?.name ? item.images[0].name : Images.NoImage}
                                                    alt={item?.name || 'User'}
                                                />
                                            </div>
                                            <div className="dash-viewer-body">
                                                <h6 className="text-capitalize mb-1">{item?.name || 'Unknown'}</h6>
                                                <span className="dash-viewer-pid">@{item?.profileId || '—'}</span>
                                                <p>
                                                    {[
                                                        item?.age ? `${item.age} yrs` : null,
                                                        item?.height ? HeightCalculate(item.height) : null,
                                                        item?.city || null,
                                                    ].filter(Boolean).join(' • ') || 'Details not added'}
                                                </p>
                                            </div>
                                        </Link>
                                    </SwiperSlide>
                                ))}
                            </Swiper>
                        </div>
                    ) : (
                        <div className="dash-empty">
                            <Icon icon={eye} size={20} />
                            <p>No one has viewed your profile yet.</p>
                        </div>
                    )}
                </section>

                {/* ============ ADVERTISEMENT ============ */}
                {user.addsense ? (
                    <section className="dash-ads">
                        <img src={user.addsense} alt="Advertisement" />
                    </section>
                ) : null}

            </div>

            {show ? <ImageCrop show={show} onHide={() => setShow(false)} image={user.image ? user.image : null} setPageload={setPageload} /> : null}
        </div>
    );
};

export default Index;
